<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <app-input
          @input.native="onGroupsSearch"
          v-model="search"
          class="input--300"
          type="search"
          placeholder="Search by id, group name, client"
          name="search"
        />
        <app-filters
          v-model="status_filter"
          :filters="status_filters"
          name="filterStatus"
          @change="onChangeFilter"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__id><div class="table-default__left">ID</div></template>
        <template #h__client.name><div class="table-default__left">Client</div></template>
        <template #h__climbing_group.name><div class="table-default__left">Group</div></template>
        <template #id="{ row }">
          <div class="table-default__left">
            <router-link :to="{name: 'rentals-detail', params: {id: row.id }}" class="table-default__link-border">
              Rental - {{ row.id }}
            </router-link>
          </div>
        </template>
        <template #client.name="{ row }">
          <div class="table-default__left table-default__left--flex">
            <template v-if="row.client">
              <router-link
                :to="{ name: 'clients-detail', params: { id: row.client.client.id } }"
                class="table-default__link-border"
              >
                {{ row.client.client.full_name }}
              </router-link>
              <span
                v-if="row.resupply && !row.resupply_fee"
                class="status-tag"
                :style="{ 'backgroundColor': '#f0c427' }"
              >
                Resupply
              </span>
              <span
                v-if="row.resupply && row.resupply_fee"
                class="status-tag"
                :style="{ 'backgroundColor': '#77b017' }"
              >
                Resupply
              </span>
              <span
                v-if="!row.resupply && row.resupply_fee"
                class="status-tag"
                :style="{ 'backgroundColor': '#e85c20' }"
              >
                Resupply
              </span>
            </template>
          </div>
        </template>
        <template #climbing_group.name="{ row }">
          <div class="table-default__left">
            <router-link :to="{name: 'climbing-groups-detail-clients', params: {id: row.client.group.id }}" class="table-default__link-border">
              {{ row.client.group.name }}
            </router-link>
          </div>
        </template>
        <template #rental_start="{ row }">
          {{ dateFormat(row.rental_start) }}
        </template>
        <template #rental_end="{ row }">
          {{ dateFormat(row.rental_end) }}
        </template>
        <template #amount="{ row }">
          <span
            v-if="row.discount_amount && row.discount_amount > 0"
            class="status-tag"
            :style="{ 'backgroundColor': '#77b017' }"
            v-tooltip="{ content: `Discount: ${row.discount_amount}$<br>${row.discount_description || ''}`, placement: 'left', offset: 10, html: true }"
          >
            {{ row.amount }}$
          </span>
          <span
            v-else
            v-tooltip="{ content: amountTooltipContent(row), placement: 'left', offset: 10, html: true }"
          >
            {{ row.amount }}$
          </span>
        </template>
        <template #balance="{ row }">
          <span
            class="status-tag"
            :style="{ 'backgroundColor': row.balance === 0 ? '#77b017' : row.balance > 0 ? '#f0c427' :'#e85c20' }"
          >
            {{ row.balance === 0 ? 'Paid': `${row.balance}$` }}
          </span>
        </template>
        <template #returned="{ row }">
          <span
            class="status-tag"
            :style="{ 'backgroundColor': row.rental_items === row.rental_items_returned ? '#77b017' : '#e85c20' }"
          >
            {{ row.rental_items }} / {{ row.rental_items_returned }}
          </span>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { getRentals } from '@/http'
import { format, parse, parseISO } from 'date-fns'

export default {
  name: 'RentalsCompleted',
  data() {
    return {
      is_loaded: false,
      search: '',
      table: {
        items: [],
        columns: ['id', 'client.name', 'climbing_group.name', 'rental_start', 'rental_end', 'amount', 'balance', 'returned'],
        options: {
          headings: {
            'id': 'ID',
            'client.name': 'Client',
            'climbing_group.name': 'Group',
            'rental_start': 'Start',
            'rental_end': 'End',
            'amount': 'Amount',
            'balance': 'Balance',
            'returned': 'Returned',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      status_filters: [
        { value: '', label: 'All' },
        { value: 'unpaid', label: 'Not paid' }
      ],
      status_filter: '',
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    onChangeFilter() {
      this.search = ''
      this.table_preloader = true
      this.pagination.page = 1
      this.fetchItems()
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    fetchItems() {
      getRentals({
        page: this.pagination.page,
        page_size: 20,
        status: 'completed',
        search: this.search,
        ordering: '-rental_end',
        paid: this.status_filter === 'unpaid' ? false : ''
      }).then(res => {
        this.table.items = res.data.results
        this.pagination.pages = res.data.pages
        this.pagination.count = res.data.count
        this.is_loaded = true
        this.table_preloader = false
      })
    },
    onGroupsSearch() {
      this.searchGroups(this)
    },
    searchGroups: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : ''
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : ''
    },
    amountTooltipContent(data) {
      let content = `Rental: ${data.amount_rental}$<br>Purchase: ${data.amount_sale}$`
      if (data.resupply_fee) content += '<br>Resupply: 50$'
      if (data.discount_method) content += `<br>Discount: ${data.discount_method.code === 'basic' ? `${data.discount}$` : `${data.discount}%`}`
      return content
    },
  }
}
</script>
